import { useState, useMemo, useEffect } from 'react';
import DateField from './DateField.jsx';
import {
  Plus, Search, Eye, Edit2, Trash2, X, Save, Download, Send, Copy, Check,
  DollarSign, Clock, AlertCircle, CheckCircle, FileText, Receipt,
  CreditCard, Calendar, User, ChevronDown, ChevronRight, Printer, Mail,
  TrendingUp, TrendingDown, Filter,
} from 'lucide-react';
import { useLocalStorage } from '../hooks.js';
import {
  createInvoice as apiCreateInvoice,
  updateInvoice as apiUpdateInvoice,
  sendInvoice as apiSendInvoice,
  payInvoiceCheckout as apiPayInvoiceCheckout,
  invoicePdfPublicUrl,
  downloadInvoicePdfAuthed,
} from '../api.js';
import { toast } from './Toast.jsx';
import { confirmDialog, ClientSearchSelect, NoteEditor, RichNote, stripNoteMarks } from './ui.jsx';

const STATUS_META = {
  draft:    { label: { en: 'Draft',     es: 'Borrador'  }, color: '#7aa3c0', bg: 'var(--ci-bd-12)', dot: '#7aa3c0' },
  sent:     { label: { en: 'Sent',      es: 'Enviada'   }, color: '#00d4ff', bg: 'rgba(0,212,255,.12)',   dot: '#00d4ff' },
  viewed:   { label: { en: 'Viewed',    es: 'Vista'     }, color: '#a78bfa', bg: 'rgba(167,139,250,.12)', dot: '#a78bfa' },
  paid:     { label: { en: 'Paid',      es: 'Pagada'    }, color: '#00ff88', bg: 'rgba(0,255,136,.12)',   dot: '#00ff88' },
  partial:  { label: { en: 'Partial',   es: 'Parcial'   }, color: '#ffb700', bg: 'rgba(255,183,0,.12)',   dot: '#ffb700' },
  overdue:  { label: { en: 'Overdue',   es: 'Vencida'   }, color: '#ff3d6b', bg: 'rgba(255,61,107,.12)',  dot: '#ff3d6b' },
  refunded: { label: { en: 'Refunded',  es: 'Reembolsada' }, color: '#f472b6', bg: 'rgba(244,114,182,.12)', dot: '#f472b6' },
  void:     { label: { en: 'Void',      es: 'Anulada'   }, color: '#64748b', bg: 'rgba(100,116,139,.12)', dot: '#64748b' },
};

const PAYMENT_METHODS = [
  { id: 'card',     icon: '💳', label: { en: 'Card',          es: 'Tarjeta'         } },
  { id: 'cash',     icon: '💵', label: { en: 'Cash',          es: 'Efectivo'        } },
  { id: 'check',    icon: '📄', label: { en: 'Check',         es: 'Cheque'          } },
  { id: 'transfer', icon: '🏦', label: { en: 'Bank Transfer', es: 'Transferencia'   } },
  { id: 'zelle',    icon: '⚡', label: { en: 'Zelle',         es: 'Zelle'           } },
  { id: 'other',    icon: '📦', label: { en: 'Other',         es: 'Otro'            } },
];

const fmt$ = (n) => {
  const num = parseFloat(n || 0);
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};
// LOCAL today as 'YYYY-MM-DD'. toISOString() returns the UTC date, which in
// Florida evenings (after ~7-8pm) rolls to tomorrow — that mis-set invoice
// issue/due dates and flagged invoices due today as "overdue"
// (feedback_local_day_not_utc). Build from local getters instead.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const addDays = (d, n) => {
  const dt = new Date(d + 'T00:00:00');
  dt.setDate(dt.getDate() + n);
  // Build from LOCAL getters — toISOString() returns the UTC date and can
  // shift the day across the offset boundary (feedback_local_day_not_utc).
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const daysBetween = (a, b) => Math.floor((new Date(b) - new Date(a)) / 86400000);

const newInvoiceNumber = (existing) => {
  const yr = new Date().getFullYear();
  const taken = (existing || [])
    .map((i) => (i.number || '').match(new RegExp(`^INV-${yr}-(\\d+)$`)))
    .filter(Boolean)
    .map((m) => parseInt(m[1], 10));
  const next = taken.length ? Math.max(...taken) + 1 : 1;
  return `INV-${yr}-${String(next).padStart(4, '0')}`;
};

export const calcTotals = (inv) => {
  const items = inv.items || [];
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.rate || 0)), 0);
  const discountAmt = inv.discountType === 'percent'
    ? subtotal * (parseFloat(inv.discount || 0) / 100)
    : parseFloat(inv.discount || 0);
  const afterDiscount = Math.max(0, subtotal - discountAmt);
  const taxAmt = afterDiscount * (parseFloat(inv.taxRate || 0) / 100);
  const total = afterDiscount + taxAmt;
  const paid = (inv.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const balance = total - paid;
  return { subtotal, discountAmt, afterDiscount, taxAmt, total, paid, balance };
};

export const computeStatus = (inv) => {
  if (!inv) return 'draft';
  if (inv.status === 'void' || inv.status === 'refunded' || inv.status === 'draft') return inv.status;
  const { total, paid } = calcTotals(inv);
  if (paid >= total - 0.005 && total > 0) return 'paid';
  if (paid > 0) return 'partial';
  if (inv.dueDate && new Date(inv.dueDate) < new Date(todayStr())) return 'overdue';
  if (inv.viewedAt) return 'viewed';
  if (inv.sentAt) return 'sent';
  return 'draft';
};

const blankInvoice = (clients) => ({
  id: '',
  number: '',
  clientId: clients?.[0]?.id || '',
  recipientName: '',
  recipientEmail: '',
  issueDate: todayStr(),
  dueDate: addDays(todayStr(), 14),
  status: 'draft',
  currency: 'USD',
  items: [
    { id: 'i' + Date.now(), description: '', qty: 1, rate: 0 },
  ],
  discount: 0,
  discountType: 'amount', // 'amount' | 'percent'
  taxRate: 0,
  notes: '',
  terms: 'Net 14',
  payments: [],
  createdAt: new Date().toISOString(),
});

export function InvoicesView({ clients = [], policies = [], invoices: invoicesProp, onInvoicesChange, currentUser, lang }) {
  const es = lang === 'es';
  // When the parent (deskte-full) wires server-backed invoices, `invoicesProp`
  // + `onInvoicesChange` are passed. Fall back to the previous localStorage
  // path in demo mode (no auth) so nothing breaks running without the API.
  const [localInvoices, setLocalInvoices] = useLocalStorage('dkt6_invoices', []);
  const invoices = invoicesProp ?? localInvoices;
  const setInvoices = onInvoicesChange || setLocalInvoices;
  const [view, setView] = useState('list'); // list | edit | detail
  const [editing, setEditing] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentModal, setPaymentModal] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Derived list with computed status
  const enriched = useMemo(() => {
    return invoices.map((inv) => ({ ...inv, _status: computeStatus(inv), _totals: calcTotals(inv) }));
  }, [invoices]);

  const filtered = useMemo(() => {
    return enriched
      .filter((i) => {
        if (statusFilter !== 'all' && i._status !== statusFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        const c = clients.find((x) => x.id === i.clientId);
        const name = c ? `${c.firstName} ${c.lastName}` : i.recipientName || '';
        return (i.number || '').toLowerCase().includes(q) || name.toLowerCase().includes(q);
      })
      .sort((a, b) => (b.issueDate || '').localeCompare(a.issueDate || ''));
  }, [enriched, statusFilter, search, clients]);

  // Top KPIs
  const kpis = useMemo(() => {
    let total = 0, paid = 0, outstanding = 0, overdue = 0;
    let countOverdue = 0, countDraft = 0, countPaid = 0;
    enriched.forEach((i) => {
      total += i._totals.total;
      paid += i._totals.paid;
      outstanding += i._totals.balance;
      if (i._status === 'overdue') { overdue += i._totals.balance; countOverdue++; }
      if (i._status === 'draft') countDraft++;
      if (i._status === 'paid') countPaid++;
    });
    return { total, paid, outstanding, overdue, countOverdue, countDraft, countPaid, countAll: enriched.length };
  }, [enriched]);

  const openNew = () => {
    const draft = blankInvoice(clients);
    draft.id = 'inv_' + Date.now();
    draft.number = newInvoiceNumber(invoices);
    setEditing(draft);
    setView('edit');
  };

  const openEdit = (inv) => {
    setEditing(JSON.parse(JSON.stringify(inv)));
    setView('edit');
  };

  const openDetail = (inv) => {
    setActiveId(inv.id);
    setView('detail');
  };

  const saveInvoice = () => {
    if (!editing) return;
    setInvoices((prev) => {
      const idx = prev.findIndex((i) => i.id === editing.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...editing, updatedAt: new Date().toISOString() };
        return copy;
      }
      return [...prev, editing];
    });
    setView('list');
    setEditing(null);
  };

  const deleteInvoice = async (id) => {
    if (!(await confirmDialog({ message: es ? '¿Eliminar esta factura?' : 'Delete this invoice?', danger: true }))) return;
    setInvoices((prev) => prev.filter((i) => i.id !== id));
    if (activeId === id) { setView('list'); setActiveId(null); }
  };

  const duplicateInvoice = (inv) => {
    const copy = JSON.parse(JSON.stringify(inv));
    copy.id = 'inv_' + Date.now();
    copy.number = newInvoiceNumber(invoices);
    copy.issueDate = todayStr();
    copy.dueDate = addDays(todayStr(), 14);
    copy.status = 'draft';
    copy.sentAt = null; copy.viewedAt = null;
    copy.payments = [];
    setInvoices((prev) => [...prev, copy]);
  };

  // Push the local invoice to the server (create or update) so /send + /pay
  // have an authoritative copy. Returns the server invoice (with serverId
  // and publicToken on success). Caches serverId on the local row so future
  // syncs update instead of duplicating.
  const syncToServer = async (inv) => {
    const c = clients.find((x) => x.id === inv.clientId);
    const recipientName = c ? `${c.firstName} ${c.lastName}` : (inv.recipientName || 'Client');
    const recipientEmail = c?.email || inv.recipientEmail || null;
    const totals = calcTotals(inv);
    const payload = {
      recipientName,
      recipientEmail,
      title: inv.title || `Invoice ${inv.number}`,
      notes: inv.notes || null,
      // Map local 'percent 0-100' to backend 'decimal 0-1'.
      taxRate: parseFloat(inv.taxRate || 0) / 100,
      dueDate: inv.dueDate || null,
      items: (inv.items || []).map((it) => ({
        description: it.description || '',
        quantity: parseFloat(it.qty || 0),
        unitPrice: parseFloat(it.rate || 0),
      })),
      clientId: inv.clientId || null,
    };
    const server = inv.serverId
      ? await apiUpdateInvoice(inv.serverId, payload)
      : await apiCreateInvoice(payload);
    // Cache the server id + publicToken on the local row.
    setInvoices((prev) => prev.map((i) => i.id === inv.id
      ? { ...i, serverId: server.id, publicToken: server.publicToken || i.publicToken, serverNumber: server.number }
      : i));
    return server;
  };

  const markSent = async (inv) => {
    try {
      const server = await syncToServer(inv);
      const result = await apiSendInvoice(server.id);
      const url = result.landingUrl || (result.publicToken ? `${window.location.origin}/invoice/${result.publicToken}` : null);
      setInvoices((prev) => prev.map((i) => i.id === inv.id
        ? { ...i, status: 'sent', sentAt: new Date().toISOString(), publicToken: result.publicToken || i.publicToken }
        : i));
      if (result.emailSent) {
        toast.success(es ? 'Factura enviada por email' : 'Invoice emailed', { link: url });
      } else {
        toast.warn(es ? 'Factura creada pero el email no se envió. Comparte el link manualmente.' : 'Invoice created but email send failed. Share the link manually.', { link: url });
      }
    } catch (e) {
      toast.error((es ? 'Error al enviar: ' : 'Send failed: ') + (e.message || String(e)));
    }
  };

  // Open the server-rendered PDF preview in a new tab. Syncs to server first
  // if needed (the PDF endpoint requires the invoice to exist server-side).
  const previewPdf = async (inv) => {
    try {
      const server = inv.serverId ? { id: inv.serverId } : await syncToServer(inv);
      const url = await downloadInvoicePdfAuthed(server.id);
      const w = window.open(url, '_blank');
      if (!w) {
        // Popup blocked — fall back to creating a download link.
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${inv.number || server.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Free the blob URL after a couple minutes — the new tab keeps a
      // reference, so revoking immediately would break it.
      setTimeout(() => URL.revokeObjectURL(url), 2 * 60 * 1000);
    } catch (e) {
      toast.error((es ? 'No se pudo generar el PDF: ' : 'PDF preview failed: ') + (e.message || String(e)));
    }
  };

  // Generate a Stripe Checkout link the agent can copy/paste or hand to the client.
  const getPayLink = async (inv) => {
    try {
      const server = inv.serverId ? { id: inv.serverId } : await syncToServer(inv);
      const url = await apiPayInvoiceCheckout(server.id);
      // Open Stripe Checkout directly. Agent can also copy from address bar.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error((es ? 'Error generando link de pago: ' : 'Pay link failed: ') + (e.message || String(e)));
    }
  };

  const markVoid = async (inv) => {
    if (!(await confirmDialog({ message: es ? '¿Anular esta factura?' : 'Void this invoice?', danger: true }))) return;
    setInvoices((prev) => prev.map((i) => i.id === inv.id ? { ...i, status: 'void' } : i));
  };

  const recordPayment = (paymentData) => {
    if (!paymentModal) return;
    const payment = {
      id: 'pay_' + Date.now(),
      ...paymentData,
      recordedAt: new Date().toISOString(),
    };
    setInvoices((prev) => prev.map((i) => i.id === paymentModal.id
      ? { ...i, payments: [...(i.payments || []), payment] }
      : i));
    setPaymentModal(null);
  };

  const copyShareLink = async (inv) => {
    // If we have a publicToken from a prior /send, use it. Otherwise sync
    // + send first so the link is real (otherwise the recipient would 404).
    let token = inv.publicToken;
    if (!token) {
      try {
        const server = await syncToServer(inv);
        const result = await apiSendInvoice(server.id);
        token = result.publicToken;
      } catch (e) {
        toast.error((es ? 'No se pudo generar el link: ' : 'Failed to generate link: ') + (e.message || String(e)));
        return;
      }
    }
    const url = `${window.location.origin}/invoice/${token}`;
    try { await navigator.clipboard.writeText(url); } catch {}
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 1800);
  };

  const printInvoice = (inv) => {
    const w = window.open('', '_blank');
    if (!w) return;
    // Defense in depth: severs window.opener so the print window cannot
    // reach the parent's __deskteAuth bearer even if the escape below
    // ever slips. Audit fix C3 (2026-05-20).
    try { w.opener = null; } catch {}
    // HTML-escape every user-controlled value before it lands in
    // document.write. The print window is same-origin with the app; an
    // unescaped client name / item description / notes field with a
    // `<script>` or `<img onerror>` payload would otherwise run in the
    // authenticated session and exfiltrate the bearer token.
    const h = (v) => {
      if (v === null || v === undefined) return '';
      return String(v)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };
    const c = clients.find((x) => x.id === inv.clientId);
    const name = c ? `${c.firstName} ${c.lastName}` : inv.recipientName || 'Client';
    const t = calcTotals(inv);
    const html = `<!doctype html><html><head><title>${h(inv.number)}</title>
      <style>
        body { font-family: -apple-system, Helvetica, Arial, sans-serif; padding: 40px; color:#111; }
        h1 { margin:0; font-size:36px; letter-spacing:-.02em; }
        .row { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:32px; }
        .lbl { font-size:11px; color:#6b7280; text-transform:uppercase; letter-spacing:.08em; margin-bottom:4px; }
        table { width:100%; border-collapse:collapse; margin:24px 0; }
        th { text-align:left; font-size:11px; color:#6b7280; text-transform:uppercase; letter-spacing:.08em; padding:8px 0; border-bottom:2px solid #111; }
        td { padding:10px 0; border-bottom:1px solid #e5e7eb; font-size:14px; }
        .amt { text-align:right; }
        .total { font-size:24px; font-weight:800; }
        .badge { display:inline-block; padding:4px 12px; border-radius:99px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
      </style></head><body>
      <div class="row">
        <div>
          <h1>INVOICE</h1>
          <div style="margin-top:4px; font-size:14px; color:#6b7280;">${h(inv.number)}</div>
        </div>
        <div style="text-align:right;">
          <div class="lbl">Issue Date</div>
          <div style="font-weight:600;">${fmtDate(inv.issueDate)}</div>
          <div class="lbl" style="margin-top:12px;">Due Date</div>
          <div style="font-weight:600;">${fmtDate(inv.dueDate)}</div>
        </div>
      </div>
      <div class="row">
        <div>
          <div class="lbl">From</div>
          <div style="font-weight:700; font-size:16px;">${h(currentUser?.agency?.name || currentUser?.name || 'Deskte CRM')}</div>
          ${currentUser?.email ? `<div style="font-size:13px; color:#6b7280;">${h(currentUser.email)}</div>` : ''}
        </div>
        <div style="text-align:right;">
          <div class="lbl">Bill To</div>
          <div style="font-weight:700; font-size:16px;">${h(name)}</div>
          ${c?.email ? `<div style="font-size:13px; color:#6b7280;">${h(c.email)}</div>` : ''}
        </div>
      </div>
      <table>
        <thead>
          <tr><th>Description</th><th class="amt" style="width:80px;">Qty</th><th class="amt" style="width:120px;">Rate</th><th class="amt" style="width:120px;">Amount</th></tr>
        </thead>
        <tbody>
          ${(inv.items || []).map((it) => `
            <tr>
              <td>${h(it.description || '')}</td>
              <td class="amt">${h(it.qty)}</td>
              <td class="amt">${fmt$(it.rate)}</td>
              <td class="amt">${fmt$((it.qty || 0) * (it.rate || 0))}</td>
            </tr>`).join('')}
        </tbody>
      </table>
      <div style="display:flex; justify-content:flex-end;">
        <table style="width:300px;">
          <tr><td>Subtotal</td><td class="amt">${fmt$(t.subtotal)}</td></tr>
          ${t.discountAmt > 0 ? `<tr><td>Discount</td><td class="amt">-${fmt$(t.discountAmt)}</td></tr>` : ''}
          ${t.taxAmt > 0 ? `<tr><td>Tax (${h(inv.taxRate)}%)</td><td class="amt">${fmt$(t.taxAmt)}</td></tr>` : ''}
          <tr><td class="total">Total</td><td class="amt total">${fmt$(t.total)}</td></tr>
          ${t.paid > 0 ? `<tr><td>Paid</td><td class="amt" style="color:#059669;">-${fmt$(t.paid)}</td></tr>` : ''}
          ${t.balance > 0.01 && t.paid > 0 ? `<tr><td style="font-weight:700;">Balance Due</td><td class="amt" style="font-weight:700;">${fmt$(t.balance)}</td></tr>` : ''}
        </table>
      </div>
      ${inv.notes ? `<div style="margin-top:32px;"><div class="lbl">Notes</div><div style="font-size:14px; line-height:1.5;">${h(stripNoteMarks(inv.notes))}</div></div>` : ''}
      ${inv.terms ? `<div style="margin-top:16px; font-size:12px; color:#6b7280;">${h(inv.terms)}</div>` : ''}
    </body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 350);
  };

  // ─── EDITOR ───
  if (view === 'edit' && editing) {
    return (
      <InvoiceEditor
        invoice={editing}
        clients={clients}
        policies={policies}
        lang={lang}
        onChange={setEditing}
        onSave={saveInvoice}
        onCancel={() => { setView('list'); setEditing(null); }}
      />
    );
  }

  // ─── DETAIL ───
  if (view === 'detail' && activeId) {
    const inv = invoices.find((i) => i.id === activeId);
    if (!inv) { setView('list'); return null; }
    return (
      <InvoiceDetail
        invoice={inv}
        clients={clients}
        currentUser={currentUser}
        lang={lang}
        onBack={() => setView('list')}
        onEdit={() => openEdit(inv)}
        onDuplicate={() => duplicateInvoice(inv)}
        onDelete={() => deleteInvoice(inv.id)}
        onMarkSent={() => markSent(inv)}
        onVoid={() => markVoid(inv)}
        onRecordPayment={() => setPaymentModal(inv)}
        onCopyLink={() => copyShareLink(inv)}
        onPayLink={() => getPayLink(inv)}
        onPdfPreview={() => previewPdf(inv)}
        onPrint={() => printInvoice(inv)}
        linkCopied={linkCopied}
        paymentModal={paymentModal}
        onRecordPaymentSubmit={recordPayment}
        onClosePaymentModal={() => setPaymentModal(null)}
      />
    );
  }

  // ─── LIST ───
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', margin: 0, letterSpacing: '-.01em' }}>
            {es ? 'Facturas' : 'Invoices'}
          </h2>
          <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>
            {es ? 'Crea, envía y rastrea pagos profesionales' : 'Create, send and track professional payments'}
          </div>
        </div>
        <button type="button" onClick={openNew}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#00d4ff,#0099cc)', border: 'none', color: '#000', fontSize: '.82rem', fontWeight: 800, padding: '.6rem 1.2rem', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(0,212,255,.25)' }}>
          <Plus size={16} />{es ? 'Nueva Factura' : 'New Invoice'}
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '.65rem', marginBottom: '1.25rem' }}>
        <KpiCard
          icon={<DollarSign size={18} />}
          label={es ? 'Total Facturado' : 'Total Billed'}
          value={fmt$(kpis.total)}
          accent="#00d4ff"
          sub={`${kpis.countAll} ${es ? 'facturas' : 'invoices'}`}
        />
        <KpiCard
          icon={<CheckCircle size={18} />}
          label={es ? 'Cobrado' : 'Collected'}
          value={fmt$(kpis.paid)}
          accent="#00ff88"
          sub={`${kpis.countPaid} ${es ? 'pagadas' : 'paid'}`}
        />
        <KpiCard
          icon={<Clock size={18} />}
          label={es ? 'Pendiente' : 'Outstanding'}
          value={fmt$(kpis.outstanding)}
          accent="#ffb700"
        />
        <KpiCard
          icon={<AlertCircle size={18} />}
          label={es ? 'Vencido' : 'Overdue'}
          value={fmt$(kpis.overdue)}
          accent="#ff3d6b"
          sub={`${kpis.countOverdue} ${es ? 'facturas' : 'invoices'}`}
          highlight={kpis.countOverdue > 0}
        />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.75rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 380 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text3,#45688a)', pointerEvents: 'none' }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={es ? 'Buscar por número o cliente...' : 'Search by number or client...'}
            style={{ width: '100%', padding: '.55rem .8rem .55rem 36px', borderRadius: 10, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', fontSize: '.82rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            ['all',     es ? 'Todas'    : 'All'],
            ['draft',   es ? 'Borrador' : 'Draft'],
            ['sent',    es ? 'Enviadas' : 'Sent'],
            ['partial', es ? 'Parcial'  : 'Partial'],
            ['paid',    es ? 'Pagadas'  : 'Paid'],
            ['overdue', es ? 'Vencidas' : 'Overdue'],
          ].map(([id, lbl]) => (
            <button key={id} type="button" onClick={() => setStatusFilter(id)}
              style={{ padding: '.4rem .85rem', borderRadius: 99, border: `1px solid ${statusFilter === id ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, background: statusFilter === id ? 'rgba(0,212,255,.12)' : 'transparent', color: statusFilter === id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 1rem', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 14 }}>
          <Receipt size={48} style={{ color: 'var(--c-text3,#45688a)', marginBottom: '.75rem', opacity: .6 }} />
          <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: 4 }}>
            {invoices.length === 0
              ? (es ? 'No tienes facturas aún' : 'No invoices yet')
              : (es ? 'Sin resultados' : 'No matches')}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', marginBottom: '1rem' }}>
            {invoices.length === 0
              ? (es ? 'Crea tu primera factura para comenzar' : 'Create your first invoice to get started')
              : (es ? 'Intenta con otros filtros' : 'Try different filters')}
          </div>
          {invoices.length === 0 && (
            <button type="button" onClick={openNew}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#00d4ff,#0099cc)', border: 'none', color: '#000', fontSize: '.82rem', fontWeight: 800, padding: '.55rem 1.2rem', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
              <Plus size={16} />{es ? 'Crear Primera Factura' : 'Create First Invoice'}
            </button>
          )}
        </div>
      )}

      {/* Invoice cards */}
      <div style={{ display: 'grid', gap: '.55rem' }}>
        {filtered.map((inv) => (
          <InvoiceCard
            key={inv.id}
            invoice={inv}
            clients={clients}
            lang={lang}
            onOpen={() => openDetail(inv)}
            onEdit={() => openEdit(inv)}
            onSend={() => markSent(inv)}
            onDuplicate={() => duplicateInvoice(inv)}
            onDelete={() => deleteInvoice(inv.id)}
            onRecordPayment={() => setPaymentModal(inv)}
          />
        ))}
      </div>

      {paymentModal && view === 'list' && (
        <PaymentModal
          invoice={paymentModal}
          lang={lang}
          onSubmit={recordPayment}
          onClose={() => setPaymentModal(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  KpiCard
// ═══════════════════════════════════════════════════════════════════
function KpiCard({ icon, label, value, accent, sub, highlight }) {
  return (
    <div style={{
      background: highlight ? 'rgba(255,61,107,.08)' : 'var(--c-surface,#0d1c2e)',
      border: `1px solid ${highlight ? 'rgba(255,61,107,.25)' : 'var(--c-border,#1a3550)'}`,
      borderRadius: 12,
      padding: '.85rem 1rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -10, right: -10, width: 60, height: 60, borderRadius: '50%', background: accent, opacity: .06 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: accent, marginBottom: 6 }}>
        {icon}
        <span style={{ fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', letterSpacing: '-.02em' }}>{value}</div>
      {sub && <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  InvoiceCard (list row)
// ═══════════════════════════════════════════════════════════════════
function InvoiceCard({ invoice, clients, lang, onOpen, onEdit, onSend, onDuplicate, onDelete, onRecordPayment }) {
  const es = lang === 'es';
  const c = clients.find((x) => x.id === invoice.clientId);
  const name = c ? `${c.firstName} ${c.lastName}` : invoice.recipientName || (es ? 'Sin cliente' : 'No client');
  const status = invoice._status || computeStatus(invoice);
  const meta = STATUS_META[status] || STATUS_META.draft;
  const t = invoice._totals || calcTotals(invoice);
  const initials = c ? `${c.firstName?.[0] || ''}${c.lastName?.[0] || ''}`.toUpperCase() : '👤';

  const daysToDue = invoice.dueDate ? daysBetween(todayStr(), invoice.dueDate) : null;

  return (
    <div onClick={onOpen}
      style={{
        background: 'var(--c-surface,#0d1c2e)',
        border: '1px solid var(--c-border,#1a3550)',
        borderRadius: 12,
        padding: '.85rem 1rem',
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '1fr auto auto auto',
        alignItems: 'center',
        gap: '1rem',
        transition: 'border-color .15s, transform .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,.4)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'; }}>
      {/* Left: number, client, dates */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', minWidth: 0 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: 'linear-gradient(135deg, var(--c-surface3,#162d44), var(--c-surface2,#112538))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '.78rem', fontWeight: 800, color: 'var(--c-cyan,#00d4ff)',
          fontFamily: 'var(--font-display)', flexShrink: 0,
          border: '1px solid var(--c-border,#1a3550)',
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: '.62rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              {invoice.number}
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 99,
              background: meta.bg, color: meta.color,
              fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: meta.dot }} />
              {meta.label[lang] || meta.label.en}
            </span>
          </div>
          <div style={{ fontSize: '.92rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 2, display: 'flex', gap: '.65rem' }}>
            <span><Calendar size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />{fmtDate(invoice.issueDate)}</span>
            {invoice.dueDate && (
              <span style={{ color: status === 'overdue' ? '#ff3d6b' : 'var(--c-text3,#45688a)', fontWeight: status === 'overdue' ? 700 : 400 }}>
                {es ? 'Vence' : 'Due'} {fmtDate(invoice.dueDate)}
                {daysToDue !== null && status !== 'paid' && status !== 'void' && (
                  <span style={{ marginLeft: 4 }}>
                    {daysToDue < 0 ? `(${Math.abs(daysToDue)}d ${es ? 'tarde' : 'late'})` : daysToDue === 0 ? `(${es ? 'hoy' : 'today'})` : `(${daysToDue}d)`}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Middle: progress bar */}
      <div style={{ width: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ height: 5, background: 'var(--c-surface3,#162d44)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${t.total > 0 ? Math.min(100, (t.paid / t.total) * 100) : 0}%`,
            background: status === 'paid' ? 'linear-gradient(90deg,#00ff88,#00d4ff)' : 'linear-gradient(90deg,#ffb700,#ff8c00)',
            transition: 'width .3s',
          }} />
        </div>
        <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', display: 'flex', justifyContent: 'space-between' }}>
          <span>{fmt$(t.paid)}</span>
          <span>{t.total > 0 ? Math.round((t.paid / t.total) * 100) : 0}%</span>
        </div>
      </div>

      {/* Total */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', letterSpacing: '-.01em' }}>
          {fmt$(t.total)}
        </div>
        {t.balance > 0.01 && status !== 'void' && (
          <div style={{ fontSize: '.62rem', color: 'var(--c-amber,#ffb700)', fontWeight: 700 }}>
            {fmt$(t.balance)} {es ? 'pendiente' : 'due'}
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
        {status !== 'paid' && status !== 'void' && onSend && (
          <IconBtn title={es ? 'Enviar por email' : 'Send by email'} onClick={onSend} accent="#00d4ff">
            <Send size={13} />
          </IconBtn>
        )}
        {status !== 'paid' && status !== 'void' && (
          <IconBtn title={es ? 'Registrar pago' : 'Record payment'} onClick={onRecordPayment} accent="#00ff88">
            <DollarSign size={14} />
          </IconBtn>
        )}
        <IconBtn title={es ? 'Editar' : 'Edit'} onClick={onEdit}><Edit2 size={13} /></IconBtn>
        <IconBtn title={es ? 'Duplicar' : 'Duplicate'} onClick={onDuplicate}><Copy size={13} /></IconBtn>
        <IconBtn title={es ? 'Eliminar' : 'Delete'} onClick={onDelete} accent="#ff3d6b"><Trash2 size={13} /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, accent }) {
  return (
    <button type="button" onClick={onClick} title={title}
      style={{
        background: 'var(--c-surface2,#112538)',
        border: '1px solid var(--c-border,#1a3550)',
        color: accent || 'var(--c-text2,#7aa3c0)',
        cursor: 'pointer',
        padding: 7,
        borderRadius: 8,
        lineHeight: 0,
        transition: 'all .12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = accent ? `${accent}22` : 'var(--c-surface3,#162d44)';
        e.currentTarget.style.borderColor = accent || 'var(--c-cyan,#00d4ff)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--c-surface2,#112538)';
        e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)';
      }}>
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  InvoiceEditor
// ═══════════════════════════════════════════════════════════════════
function InvoiceEditor({ invoice, clients, policies, lang, onChange, onSave, onCancel }) {
  const es = lang === 'es';
  const inv = invoice;
  const set = (k, v) => onChange({ ...inv, [k]: v });
  const setItem = (idx, patch) => {
    const items = [...(inv.items || [])];
    items[idx] = { ...items[idx], ...patch };
    onChange({ ...inv, items });
  };
  const addItem = () => onChange({ ...inv, items: [...(inv.items || []), { id: 'i' + Date.now(), description: '', qty: 1, rate: 0 }] });
  const removeItem = (idx) => onChange({ ...inv, items: inv.items.filter((_, i) => i !== idx) });
  const t = calcTotals(inv);

  const insertFromPolicy = () => {
    const list = policies.filter((p) => p.clientId === inv.clientId);
    if (list.length === 0) {
      toast.warn(es ? 'Este cliente no tiene pólizas' : 'This client has no policies');
      return;
    }
    const p = list[0];
    const desc = `${p.carrier || ''} ${p.plan || p.type || ''}`.trim() || (es ? 'Servicio de seguro' : 'Insurance service');
    const items = [...(inv.items || [])];
    if (items.length === 1 && !items[0].description && !items[0].rate) {
      items[0] = { ...items[0], description: desc, rate: parseFloat(p.monthlyPremium || 0) };
    } else {
      items.push({ id: 'i' + Date.now(), description: desc, qty: 1, rate: parseFloat(p.monthlyPremium || 0) });
    }
    onChange({ ...inv, items });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.65rem' }}>
          <button type="button" onClick={onCancel}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 7, borderRadius: 8, lineHeight: 0 }}>
            <X size={14} />
          </button>
          <div>
            <div style={{ fontSize: '.65rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)' }}>
              {es ? 'Editar Factura' : 'Edit Invoice'}
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', letterSpacing: '-.01em' }}>
              {inv.number}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button type="button" onClick={onCancel}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: '.55rem 1rem', borderRadius: 9, fontSize: '.78rem', fontWeight: 700, fontFamily: 'inherit' }}>
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          <button type="button" onClick={onSave}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#00d4ff,#0099cc)', border: 'none', color: '#000', fontSize: '.82rem', fontWeight: 800, padding: '.55rem 1.2rem', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}>
            <Save size={14} />{es ? 'Guardar' : 'Save'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1rem', alignItems: 'start' }}>
        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {/* Client + dates */}
          <Section title={es ? 'Datos de la Factura' : 'Invoice Details'}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.65rem' }}>
              <Field label={es ? 'Cliente' : 'Client'}>
                <ClientSearchSelect clients={clients} value={inv.clientId} onChange={(e) => set('clientId', e.target.value)} lang={lang} noneLabel={es ? '— Cliente externo —' : '— External recipient —'} />
              </Field>
              <Field label={es ? 'Número' : 'Number'}>
                <input value={inv.number || ''} onChange={(e) => set('number', e.target.value)} className="dkt-inp" style={{ width: '100%' }} />
              </Field>
              {!inv.clientId && (
                <>
                  <Field label={es ? 'Nombre del destinatario' : 'Recipient name'}>
                    <input value={inv.recipientName || ''} onChange={(e) => set('recipientName', e.target.value)} className="dkt-inp" style={{ width: '100%' }} />
                  </Field>
                  <Field label="Email">
                    <input type="email" value={inv.recipientEmail || ''} onChange={(e) => set('recipientEmail', e.target.value)} className="dkt-inp" style={{ width: '100%' }} />
                  </Field>
                </>
              )}
              <Field label={es ? 'Fecha de emisión' : 'Issue date'}>
                <DateField value={inv.issueDate || ''} onChange={(e) => set('issueDate', e.target.value)} className="dkt-inp" style={{ width: '100%' }} />
              </Field>
              <Field label={es ? 'Fecha de vencimiento' : 'Due date'}>
                <DateField value={inv.dueDate || ''} onChange={(e) => set('dueDate', e.target.value)} className="dkt-inp" style={{ width: '100%' }} />
              </Field>
            </div>
          </Section>

          {/* Items */}
          <Section title={es ? 'Conceptos' : 'Line Items'} action={
            <div style={{ display: 'flex', gap: '.4rem' }}>
              {inv.clientId && policies.some((p) => p.clientId === inv.clientId) && (
                <button type="button" onClick={insertFromPolicy}
                  style={{ background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.3)', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', padding: '.35rem .7rem', borderRadius: 7, fontSize: '.7rem', fontWeight: 700, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FileText size={11} />{es ? 'Desde póliza' : 'From policy'}
                </button>
              )}
              <button type="button" onClick={addItem}
                style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', padding: '.35rem .7rem', borderRadius: 7, fontSize: '.7rem', fontWeight: 700, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Plus size={11} />{es ? 'Agregar' : 'Add'}
              </button>
            </div>
          }>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 100px 100px 30px', gap: 4, marginBottom: 6, padding: '0 .25rem' }}>
              <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)' }}>{es ? 'Descripción' : 'Description'}</div>
              <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{es ? 'Cant' : 'Qty'}</div>
              <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{es ? 'Precio' : 'Rate'}</div>
              <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{es ? 'Total' : 'Amount'}</div>
              <div />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(inv.items || []).map((it, idx) => (
                <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 100px 100px 30px', gap: 4, alignItems: 'center' }}>
                  <input value={it.description || ''} onChange={(e) => setItem(idx, { description: e.target.value })}
                    placeholder={es ? 'Servicio o producto...' : 'Service or product...'}
                    className="dkt-inp" style={{ width: '100%' }} />
                  <input type="number" min="0" step="0.01" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })}
                    className="dkt-inp" style={{ width: '100%', textAlign: 'right' }} />
                  <input type="number" min="0" step="0.01" value={it.rate} onChange={(e) => setItem(idx, { rate: e.target.value })}
                    className="dkt-inp" style={{ width: '100%', textAlign: 'right' }} />
                  <div style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {fmt$((parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0))}
                  </div>
                  {(inv.items.length > 1) && (
                    <button type="button" onClick={() => removeItem(idx)}
                      style={{ background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', padding: 4, lineHeight: 0 }}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* Notes & Terms */}
          <Section title={es ? 'Notas y Términos' : 'Notes & Terms'}>
            <Field label={es ? 'Notas para el cliente' : 'Notes for client'}>
              <NoteEditor value={inv.notes || ''} onChange={(v) => set('notes', v)} lang={es ? 'es' : 'en'} rows={2}
                placeholder={es ? 'Gracias por tu negocio...' : 'Thank you for your business...'}
                style={{ width: '100%' }} />
            </Field>
            <Field label={es ? 'Términos de pago' : 'Payment terms'}>
              <input value={inv.terms || ''} onChange={(e) => set('terms', e.target.value)}
                placeholder="Net 14, Due on receipt, ..."
                className="dkt-inp" style={{ width: '100%' }} />
            </Field>
          </Section>
        </div>

        {/* Sidebar — Totals */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem', position: 'sticky', top: 10 }}>
          <Section title={es ? 'Totales' : 'Totals'}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', marginBottom: '.5rem' }}>
              <Field label={es ? 'Descuento' : 'Discount'}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="number" min="0" step="0.01" value={inv.discount || 0} onChange={(e) => set('discount', e.target.value)}
                    className="dkt-inp" style={{ flex: 1 }} />
                  <select value={inv.discountType || 'amount'} onChange={(e) => set('discountType', e.target.value)}
                    className="dkt-inp" style={{ width: 50 }}>
                    <option value="amount">$</option>
                    <option value="percent">%</option>
                  </select>
                </div>
              </Field>
              <Field label={es ? 'Impuesto %' : 'Tax %'}>
                <input type="number" min="0" step="0.01" value={inv.taxRate || 0} onChange={(e) => set('taxRate', e.target.value)}
                  className="dkt-inp" style={{ width: '100%' }} />
              </Field>
            </div>
            <Divider />
            <Row label={es ? 'Subtotal' : 'Subtotal'} value={fmt$(t.subtotal)} />
            {t.discountAmt > 0 && <Row label={es ? 'Descuento' : 'Discount'} value={`-${fmt$(t.discountAmt)}`} color="#ff3d6b" />}
            {t.taxAmt > 0 && <Row label={es ? `Impuesto (${inv.taxRate}%)` : `Tax (${inv.taxRate}%)`} value={fmt$(t.taxAmt)} />}
            <Divider />
            <Row label={es ? 'TOTAL' : 'TOTAL'} value={fmt$(t.total)} bold accent="#00d4ff" big />
          </Section>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  InvoiceDetail
// ═══════════════════════════════════════════════════════════════════
function InvoiceDetail({
  invoice, clients, currentUser, lang,
  onBack, onEdit, onDuplicate, onDelete, onMarkSent, onVoid,
  onRecordPayment, onCopyLink, onPayLink, onPdfPreview, onPrint, linkCopied,
  paymentModal, onRecordPaymentSubmit, onClosePaymentModal,
}) {
  const es = lang === 'es';
  const c = clients.find((x) => x.id === invoice.clientId);
  const name = c ? `${c.firstName} ${c.lastName}` : invoice.recipientName || (es ? 'Sin cliente' : 'No client');
  const status = computeStatus(invoice);
  const meta = STATUS_META[status] || STATUS_META.draft;
  const t = calcTotals(invoice);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.65rem' }}>
          <button type="button" onClick={onBack}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 7, borderRadius: 8, lineHeight: 0 }}>
            <X size={14} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '.72rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                {invoice.number}
              </span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 99,
                background: meta.bg, color: meta.color,
                fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: meta.dot }} />
                {meta.label[lang] || meta.label.en}
              </span>
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', marginTop: 2 }}>
              {name}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          {status !== 'paid' && status !== 'void' && (
            <button type="button" onClick={onMarkSent}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,212,255,.12)', border: '1px solid rgba(0,212,255,.3)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.78rem', fontWeight: 700, padding: '.5rem .9rem', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}>
              <Send size={13} />{es ? (status === 'draft' ? 'Enviar' : 'Reenviar') : (status === 'draft' ? 'Send' : 'Resend')}
            </button>
          )}
          {status !== 'paid' && status !== 'void' && (
            <button type="button" onClick={onRecordPayment}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', color: '#000', fontSize: '.82rem', fontWeight: 800, padding: '.5rem 1rem', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,255,136,.25)' }}>
              <DollarSign size={14} />{es ? 'Cobrar' : 'Record Payment'}
            </button>
          )}
          <button type="button" onClick={onCopyLink}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.78rem', fontWeight: 700, padding: '.5rem .9rem', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}>
            {linkCopied ? <Check size={13} /> : <Copy size={13} />}
            {linkCopied ? (es ? 'Copiado' : 'Copied') : (es ? 'Copiar link' : 'Copy link')}
          </button>
          {status !== 'paid' && status !== 'void' && (
            <button type="button" onClick={onPayLink}
              title={es ? 'Abrir Stripe Checkout (link de pago)' : 'Open Stripe Checkout (pay link)'}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.3)', color: '#6366f1', fontSize: '.78rem', fontWeight: 700, padding: '.5rem .9rem', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}>
              <CreditCard size={13} />{es ? 'Link de pago' : 'Pay link'}
            </button>
          )}
          <button type="button" onClick={onPdfPreview}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 8, borderRadius: 8, lineHeight: 0 }} title={es ? 'Vista previa PDF' : 'PDF preview'}>
            <FileText size={14} />
          </button>
          <button type="button" onClick={onPrint}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 8, borderRadius: 8, lineHeight: 0 }} title={es ? 'Imprimir HTML' : 'Print HTML'}>
            <Printer size={14} />
          </button>
          <button type="button" onClick={onEdit}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 8, borderRadius: 8, lineHeight: 0 }} title={es ? 'Editar' : 'Edit'}>
            <Edit2 size={14} />
          </button>
          <button type="button" onClick={onDuplicate}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 8, borderRadius: 8, lineHeight: 0 }} title={es ? 'Duplicar' : 'Duplicate'}>
            <Copy size={14} />
          </button>
          {status !== 'void' && (
            <button type="button" onClick={onVoid}
              style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text3,#45688a)', cursor: 'pointer', padding: 8, borderRadius: 8, lineHeight: 0 }} title={es ? 'Anular' : 'Void'}>
              <AlertCircle size={14} />
            </button>
          )}
          <button type="button" onClick={onDelete}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid rgba(255,61,107,.3)', color: '#ff3d6b', cursor: 'pointer', padding: 8, borderRadius: 8, lineHeight: 0 }} title={es ? 'Eliminar' : 'Delete'}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Big card */}
      <div style={{
        background: 'linear-gradient(180deg, var(--c-surface,#0d1c2e), var(--c-surface2,#112538))',
        border: '1px solid var(--c-border,#1a3550)',
        borderRadius: 16,
        padding: '2rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }}>
          <div>
            <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
              {es ? 'De' : 'From'}
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
              {currentUser?.agency?.name || currentUser?.name || 'Deskte CRM'}
            </div>
            {currentUser?.email && <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)' }}>{currentUser.email}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
              {es ? 'Para' : 'Bill To'}
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{name}</div>
            {(c?.email || invoice.recipientEmail) && (
              <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)' }}>{c?.email || invoice.recipientEmail}</div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '.75rem', padding: '.75rem 1rem', background: 'var(--c-surface3,#162d44)', borderRadius: 10, marginBottom: '1.25rem' }}>
          <div>
            <div style={{ fontSize: '.58rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>
              {es ? 'Emisión' : 'Issued'}
            </div>
            <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{fmtDate(invoice.issueDate)}</div>
          </div>
          <div>
            <div style={{ fontSize: '.58rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>
              {es ? 'Vencimiento' : 'Due'}
            </div>
            <div style={{ fontSize: '.85rem', fontWeight: 700, color: status === 'overdue' ? '#ff3d6b' : 'var(--c-white,#eef6ff)' }}>{fmtDate(invoice.dueDate)}</div>
          </div>
          <div>
            <div style={{ fontSize: '.58rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>
              {es ? 'Términos' : 'Terms'}
            </div>
            <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{invoice.terms || '—'}</div>
          </div>
        </div>

        {/* Items table */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 110px 110px', gap: 4, padding: '.4rem 0', borderBottom: '2px solid var(--c-border,#1a3550)' }}>
            <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)' }}>{es ? 'Descripción' : 'Description'}</div>
            <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{es ? 'Cant' : 'Qty'}</div>
            <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{es ? 'Precio' : 'Rate'}</div>
            <div style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{es ? 'Total' : 'Amount'}</div>
          </div>
          {(invoice.items || []).map((it) => (
            <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 110px 110px', gap: 4, padding: '.55rem 0', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
              <div style={{ fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>{it.description}</div>
              <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{it.qty}</div>
              <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmt$(it.rate)}</div>
              <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmt$((parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0))}</div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Row label={es ? 'Subtotal' : 'Subtotal'} value={fmt$(t.subtotal)} />
            {t.discountAmt > 0 && <Row label={es ? 'Descuento' : 'Discount'} value={`-${fmt$(t.discountAmt)}`} color="#ff3d6b" />}
            {t.taxAmt > 0 && <Row label={es ? `Impuesto (${invoice.taxRate}%)` : `Tax (${invoice.taxRate}%)`} value={fmt$(t.taxAmt)} />}
            <Divider />
            <Row label={es ? 'TOTAL' : 'TOTAL'} value={fmt$(t.total)} bold big accent="#00d4ff" />
            {t.paid > 0 && <Row label={es ? 'Pagado' : 'Paid'} value={`-${fmt$(t.paid)}`} color="#00ff88" />}
            {t.balance > 0.01 && t.paid > 0 && (
              <>
                <Divider />
                <Row label={es ? 'PENDIENTE' : 'BALANCE DUE'} value={fmt$(t.balance)} bold big accent="#ffb700" />
              </>
            )}
            {t.balance <= 0.01 && t.total > 0 && (
              <div style={{ marginTop: '.5rem', padding: '.55rem .8rem', background: 'rgba(0,255,136,.1)', border: '1px solid rgba(0,255,136,.3)', borderRadius: 9, color: '#00ff88', fontSize: '.78rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                <CheckCircle size={14} />{es ? '¡Factura pagada en su totalidad!' : 'Invoice paid in full!'}
              </div>
            )}
          </div>
        </div>

        {invoice.notes && (
          <div style={{ marginTop: '1.25rem', padding: '.75rem 1rem', background: 'var(--c-surface3,#162d44)', borderRadius: 10 }}>
            <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
              {es ? 'Notas' : 'Notes'}
            </div>
            <div style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)' }}><RichNote text={invoice.notes} /></div>
          </div>
        )}
      </div>

      {/* Payments history */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1rem' }}>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: '.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <CreditCard size={13} />{es ? 'Historial de pagos' : 'Payment history'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
            {invoice.payments.map((p) => {
              const pm = PAYMENT_METHODS.find((x) => x.id === p.method) || PAYMENT_METHODS[0];
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.55rem .75rem', background: 'var(--c-surface2,#112538)', borderLeft: '3px solid #00ff88', borderRadius: 8 }}>
                  <div style={{ fontSize: '1rem' }}>{pm.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{fmt$(p.amount)}</div>
                    <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)' }}>
                      {pm.label[lang] || pm.label.en} · {fmtDate(p.date)}{p.reference ? ` · ${es ? 'Ref' : 'Ref'} ${p.reference}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {paymentModal && (
        <PaymentModal
          invoice={paymentModal}
          lang={lang}
          onSubmit={onRecordPaymentSubmit}
          onClose={onClosePaymentModal}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PaymentModal
// ═══════════════════════════════════════════════════════════════════
function PaymentModal({ invoice, lang, onSubmit, onClose }) {
  const es = lang === 'es';
  const t = calcTotals(invoice);
  const [form, setForm] = useState({
    amount: t.balance.toFixed(2),
    method: 'card',
    date: todayStr(),
    reference: '',
    notes: '',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1.5rem', maxWidth: 460, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)' }}>
              {invoice.number}
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <DollarSign size={18} style={{ color: '#00ff88' }} />{es ? 'Registrar Pago' : 'Record Payment'}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ background: 'var(--c-surface2,#112538)', borderRadius: 10, padding: '.6rem .85rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', fontSize: '.78rem' }}>
          <span style={{ color: 'var(--c-text2,#7aa3c0)' }}>{es ? 'Saldo pendiente' : 'Outstanding balance'}</span>
          <span style={{ color: 'var(--c-amber,#ffb700)', fontWeight: 800, fontFamily: 'var(--font-display)' }}>{fmt$(t.balance)}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.65rem', marginBottom: '.65rem' }}>
          <Field label={es ? 'Monto' : 'Amount'}>
            <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} className="dkt-inp" style={{ width: '100%', fontSize: '1rem', fontWeight: 700 }} autoFocus />
          </Field>
          <Field label={es ? 'Fecha' : 'Date'}>
            <DateField value={form.date} onChange={(e) => set('date', e.target.value)} className="dkt-inp" style={{ width: '100%' }} />
          </Field>
        </div>

        <Field label={es ? 'Método' : 'Method'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {PAYMENT_METHODS.map((m) => (
              <button key={m.id} type="button" onClick={() => set('method', m.id)}
                style={{ padding: '.55rem .4rem', borderRadius: 8, border: `1px solid ${form.method === m.id ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, background: form.method === m.id ? 'rgba(0,212,255,.12)' : 'var(--c-surface2,#112538)', color: form.method === m.id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <span>{m.icon}</span>{m.label[lang] || m.label.en}
              </button>
            ))}
          </div>
        </Field>

        <Field label={es ? 'Referencia (opcional)' : 'Reference (optional)'}>
          <input value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder={es ? '# de transacción, cheque...' : 'Transaction #, check...'} className="dkt-inp" style={{ width: '100%' }} />
        </Field>

        <Field label={es ? 'Notas' : 'Notes'}>
          <NoteEditor value={form.notes} onChange={(v) => set('notes', v)} lang={es ? 'es' : 'en'} rows={2} style={{ width: '100%' }} />
        </Field>

        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" onClick={onClose}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: '.55rem 1rem', borderRadius: 9, fontSize: '.78rem', fontWeight: 700, fontFamily: 'inherit' }}>
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          <button type="button" onClick={() => onSubmit(form)} disabled={!form.amount || parseFloat(form.amount) <= 0}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: !form.amount || parseFloat(form.amount) <= 0 ? '#2a4560' : 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', color: '#000', fontSize: '.82rem', fontWeight: 800, padding: '.55rem 1.2rem', borderRadius: 9, cursor: !form.amount || parseFloat(form.amount) <= 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            <Check size={14} />{es ? 'Confirmar' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════════
function Section({ title, action, children }) {
  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem' }}>
        <div style={{ fontSize: '.62rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)' }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '.55rem' }}>
      <label style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>{label}</label>
      {children}
    </div>
  );
}
function Row({ label, value, bold, big, accent, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '.2rem 0' }}>
      <span style={{ fontSize: bold ? '.78rem' : '.72rem', color: bold ? 'var(--c-white,#eef6ff)' : 'var(--c-text2,#7aa3c0)', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{
        fontSize: big ? '1.15rem' : '.85rem',
        fontWeight: bold ? 800 : 600,
        color: color || accent || 'var(--c-white,#eef6ff)',
        fontFamily: 'var(--font-display)',
        letterSpacing: '-.01em',
      }}>{value}</span>
    </div>
  );
}
function Divider() {
  return <div style={{ borderTop: '1px solid var(--c-border,#1a3550)', margin: '.3rem 0' }} />;
}
